import React, { useLayoutEffect, useRef } from 'react';
import * as StatsActions from '../../actions/StatsActions'

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { nodeModuleNameResolver } from 'typescript';
import { AmChartsLogo } from '@amcharts/amcharts4/.internal/core/elements/AmChartsLogo';
import { filter } from '@amcharts/amcharts4/.internal/core/utils/Iterator';

am4core.useTheme(am4themes_dark);
am4core.useTheme(am4themes_animated);

export interface IToggleableChordDiagramChartProps {
    id: string;
    data: Array<StatsActions.IRelationshipRankingsType>
  }

function addFilteredDataValues(
    c: am4charts.ChordDiagram,
    data: Array<StatsActions.IRelationshipRankingsType>,
    uniqueUsers: string[],
    userColorLookup: any,
    node: am4charts.ChordNode | null = null) {

    // Prep the data for the chart.
    let newData: any[] = [];

    let nodeName = node != null ? node.label.currentText : "";

    // Need to get all the new unique users that are in the view of the chart (filtered or not).
    let filteredUniqueUsers: string[] = [];
    if (node != null) {
        for (let i = 0; i < data.length; i++) {
            let item = data[i];
            if (node != null && (item.from != nodeName && item.to != nodeName)) {
                continue;
            }

            if (!filteredUniqueUsers.includes(item.from)) {
                filteredUniqueUsers.push(item.from);
            }

            if (!filteredUniqueUsers.includes(item.to)) {
                filteredUniqueUsers.push(item.to);
            }
        }
    }
    else {
        filteredUniqueUsers = uniqueUsers;
    }

    // Sort the unique users by from name, case insensitive.
    filteredUniqueUsers.sort((a, b) => {
        return a.localeCompare(b);
    })

    // Since node property fields take data from data items that are first mentioned,
    // we need to add some empty data items at the beginning for setting colors.
    for (let i = 0; i < filteredUniqueUsers.length; i++) {
        newData.push({
            from: filteredUniqueUsers[i],
            color: userColorLookup[filteredUniqueUsers[i]],
        });
    }

    // Now add the actual data values.
    for (let i = 0; i < data.length; i++) {
        let item = data[i];

        if (node != null && (item.from != nodeName && item.to != nodeName)) {
            continue;
        }

        newData.push({
            from: item.from,
            to: item.to,
            value: item.value,
        })
    }

    c.data = newData;
}

function createUniqueUsersArray(data: Array<StatsActions.IRelationshipRankingsType>) : string[] {
    let uniqueUsers: string[] = []
    for (let i = 0; i < data.length; i++) {
        let fromUserName = data[i].from;
        let toUserName = data[i].to;

        if (!uniqueUsers.includes(fromUserName)) {
            uniqueUsers.push(fromUserName);
        }
        if (!uniqueUsers.includes(toUserName)) {
            uniqueUsers.push(toUserName);
        }
    }

    return uniqueUsers;
}

function createUserColorLookup(uniqueUsers: string[], c: am4charts.ChordDiagram) : any {
    let userColorLookup: any = {}
    for (let i = 0; i < uniqueUsers.length; i++) {
        userColorLookup[uniqueUsers[i]] = c.colors.next();
    }

    return userColorLookup;
}

const ToggleableChordDiagramChart: React.FC<IToggleableChordDiagramChartProps> = (props: IToggleableChordDiagramChartProps) => {
    const chart = useRef();

    useLayoutEffect(() => {
        let c = am4core.create(`${props.id}`, am4charts.ChordDiagram);

        let uniqueUsers: string[] = createUniqueUsersArray(props.data);
        let userColorLookup: any = createUserColorLookup(uniqueUsers, c);

        chart.current = c as any;
    
        // colors of main characters
        c.colors.saturation = 0.45;
        c.colors.step = 3;

        // Prep the data for the chart.
        addFilteredDataValues(c, props.data, uniqueUsers, userColorLookup);

        c.dataFields.fromName = "from";
        c.dataFields.toName = "to";
        c.dataFields.value = "value";

        c.nodePadding = 0.5;
        c.minNodeSize = 0.01;
        c.startAngle = 80;
        c.endAngle = c.startAngle + 360;
        c.sortBy = "none";
        c.fontSize = 10;

        let nodeTemplate = c.nodes.template;
        nodeTemplate.readerTitle = "Click to show/hide or drag to rearrange";
        nodeTemplate.showSystemTooltip = true;
        nodeTemplate.propertyFields.fill = "color";
        nodeTemplate.tooltipText = "{name} took {totalOutgoing} score(s) and lost {totalIncoming} score(s)";
        nodeTemplate.draggable = false;

        // when rolled over the node, make all the links rolled-over
        nodeTemplate.events.on("over", function(event) {    
            let node = event.target;
            node.outgoingDataItems.each(function(dataItem) {
                if(dataItem.toNode){
                    dataItem.link.isHover = true;
                    (dataItem.toNode as any).label.isHover = true;
                }
            })
            node.incomingDataItems.each(function(dataItem) {
                if(dataItem.fromNode){
                    dataItem.link.isHover = true;
                    dataItem.fromNode.label.isHover = true;
                }
            }) 

            node.label.isHover = true;   
        })

        // when rolled out from the node, make all the links rolled-out
        nodeTemplate.events.on("out", function(event) {
            let node = event.target;
            node.outgoingDataItems.each(function(dataItem) {        
                if(dataItem.toNode){
                    dataItem.link.isHover = false;                
                    (dataItem.toNode as any).label.isHover = false;
                }
            })
            node.incomingDataItems.each(function(dataItem) {
                if(dataItem.fromNode){
                    dataItem.link.isHover = false;
                dataItem.fromNode.label.isHover = false;
                }
            })

            node.label.isHover = false;
        })

        let currentHitNode: string | null = null;
        nodeTemplate.events.on("hit", function(event) {
            let node = event.target;
            let shouldExpand = currentHitNode == node.label.currentText;

            if (shouldExpand) {
                currentHitNode = null;
            }
            else {
                currentHitNode = node.label.currentText;
            }

            if (shouldExpand) {
                addFilteredDataValues(c, props.data, uniqueUsers, userColorLookup);
            }
            else {
                addFilteredDataValues(c, props.data, uniqueUsers, userColorLookup, node);
            };
        })

        let label = nodeTemplate.label;
        label.relativeRotation = 90;

        label.fillOpacity = 0.4;
        let labelHS = label.states.create("hover");
        labelHS.properties.fillOpacity = 1;

        nodeTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;

        // link template
        let linkTemplate = c.links.template;
        linkTemplate.strokeOpacity = 0;
        linkTemplate.fillOpacity = 0.15;
        linkTemplate.tooltipText = "{fromName} took {value.value} score(s) from {toName}";

        let hoverState = linkTemplate.states.create("hover");
        hoverState.properties.fillOpacity = 0.7;
        hoverState.properties.strokeOpacity = 0.7;
    
        return () => {
            c.dispose();
        };
      }, [props]);
    
    return (
        <div className="ToggleableChordDiagramChart" id={props.id} style={{ width: "100%", height: "1200px" }}></div>
    );
  }
  
  export default ToggleableChordDiagramChart;